import { useEffect, useRef, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { MEMBER_ELECTIONS_QUERY } from "../queries";
import type { MemberElectionsQueryResponse, MemberElectionsQueryVariables } from "../types";
import { UPCOMING_MEMBER_ELECTION_DATES_QUERY } from "../upcoming-dates/queries";
import type { UpcomingMemberElectionDatesQueryResponse } from "../upcoming-dates/types";

type UseMemberElectionsListArgs = {
  deselectAll: () => void;
};

/**
 * List/search/metric query orchestration for Member Elections.
 * Keeps the page component focused on layout and chrome.
 */
export function useMemberElectionsList({ deselectAll }: UseMemberElectionsListArgs) {
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const [cachedUnfilteredTotal, setCachedUnfilteredTotal] = useState<number | null>(null);
  const [settledSearch, setSettledSearch] = useState(debouncedSearch);

  useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      resetPagination();
      deselectAll();
    }
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, resetPagination, deselectAll]);

  const queryVariables: MemberElectionsQueryVariables = {
    ...paginationVariables,
    ...(debouncedSearch ? { search: debouncedSearch } : {})
  };

  const searchActive = !!debouncedSearch;
  const needsMetricFetch = searchActive && cachedUnfilteredTotal == null;

  const {
    data: currentData,
    previousData,
    loading,
    error,
    refetch,
    networkStatus
  } = useQuery<MemberElectionsQueryResponse, MemberElectionsQueryVariables>(MEMBER_ELECTIONS_QUERY, {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true
  });

  // Cold search (e.g. deep link) only — otherwise reuse the last unfiltered list totalCount.
  const {
    data: metricData,
    loading: metricLoading,
    error: metricError
  } = useQuery<MemberElectionsQueryResponse, MemberElectionsQueryVariables>(MEMBER_ELECTIONS_QUERY, {
    variables: { first: 1 },
    skip: !needsMetricFetch
  });

  const { data: upcomingDatesData } = useQuery<UpcomingMemberElectionDatesQueryResponse>(
    UPCOMING_MEMBER_ELECTION_DATES_QUERY
  );
  const upcomingDates = upcomingDatesData?.upcomingMemberElectionDates ?? [];

  useEffect(() => {
    if (!searchActive && currentData?.membershipApplications) {
      setCachedUnfilteredTotal(currentData.membershipApplications.totalCount);
    }
  }, [searchActive, currentData]);

  useEffect(() => {
    if (!loading && !error) {
      setSettledSearch(debouncedSearch);
    }
  }, [loading, error, debouncedSearch]);

  // Clear rows for search transitions only (not pagination), so prior applicants
  // cannot stamp under a new term while the filtered query is in flight.
  // During pagination Apollo often clears currentData while previousData still
  // holds the last page — keep that fallback so the table does not flash empty.
  const isUpdatingSearchResults = debouncedSearch !== settledSearch || networkStatus === NetworkStatus.refetch;
  const listConnection = isUpdatingSearchResults ? undefined : (currentData ?? previousData)?.membershipApplications;
  const showLoadError = !!error && !loading;
  const eligibleApplications = listConnection?.nodes ?? [];
  const listTotalCount = listConnection?.totalCount ?? 0;
  const unfilteredTotal = cachedUnfilteredTotal ?? metricData?.membershipApplications.totalCount ?? null;
  // While clearing search, prefer the cached unfiltered total over an emptied list connection.
  const totalEligible = searchActive
    ? (unfilteredTotal ?? 0)
    : isUpdatingSearchResults
      ? (cachedUnfilteredTotal ?? listTotalCount)
      : listTotalCount;
  const showMetricError = Boolean(showLoadError || (needsMetricFetch && metricError));
  const metricCardLoading =
    !showMetricError &&
    (needsMetricFetch ? metricLoading && !metricData : !searchActive && loading && !currentData && !previousData);
  const hasUpcomingDates = upcomingDates.length > 0;

  return {
    searchValue,
    setSearchValue,
    clearSearch,
    searchActive,
    createHeaderPagination: createHeaderPagination(listConnection),
    createFooterPagination: createFooterPagination(listConnection),
    loading: loading || isUpdatingSearchResults,
    eligibleApplications,
    showLoadError,
    refetch,
    totalEligible,
    showMetricError,
    metricCardLoading,
    upcomingDates,
    hasUpcomingDates
  };
}
